import { Component, createSignal, Show, Index } from 'solid-js';
import { ControlMenuItem } from './ControlMenuItem';
import { ControlMenuItemType } from './types';
import { t } from '@trimble/i18n';

interface ControlMenuProps {
  items: ControlMenuItemType[];
  parentOpensUpward?: boolean;
  onClose?(): void;
}

const isTouchDevice = (): boolean => {
  return window.navigator.maxTouchPoints > 0;
};

export const ControlMenu: Component<ControlMenuProps> = (props) => {
  const isMobile = isTouchDevice();
  const [navigationPath, setNavigationPath] = createSignal<
    ControlMenuItemType[]
  >([]);
  const [openMenus, setOpenMenus] = createSignal<Set<string>>(new Set());

  const getChildren = (item: ControlMenuItemType): ControlMenuItemType[] => {
    return 'children' in item ? (item.children ?? []) : [];
  };

  const navigateToSubmenu = (item: ControlMenuItemType): void => {
    if (getChildren(item).length > 0) {
      setNavigationPath([...navigationPath(), item]);
    }
  };

  const navigateBack = (): void => {
    const path = navigationPath();
    if (path.length > 0) {
      const newPath = path.slice(0, -1);
      setNavigationPath(newPath);
    }
  };

  const currentItems = (): ControlMenuItemType[] => {
    const path = navigationPath();
    if (path.length === 0) {
      return props.items;
    }
    let current: ControlMenuItemType[] = props.items;
    for (const pathItem of path) {
      const found = current.find((item) => item.id === pathItem.id);
      if (found && getChildren(found).length > 0) {
        // Item found in current list - use its children
        current = getChildren(found);
      } else if (getChildren(pathItem).length > 0) {
        // Item not found but pathItem has children directly (e.g., radio option submenu)
        current = getChildren(pathItem);
      } else {
        return [];
      }
    }
    return current;
  };

  return (
    <div class="bg-base-page min-w-[150px] p-2 select-none rounded-sm overflow-visible shadow-lg">
      <Show when={isMobile && navigationPath().length > 0}>
        <div
          class="flex items-center gap-1 cursor-pointer"
          onClick={navigateBack}
        >
          <modus-wc-icon name="chevron_left" />
          <span class="text-base-content">{t('general.back')}</span>
        </div>
      </Show>
      <Index each={currentItems()}>
        {(item) => (
          <ControlMenuItem
            item={item()}
            isMobile={isMobile}
            openMenus={openMenus()}
            setOpenMenus={setOpenMenus}
            onNavigate={navigateToSubmenu}
            onClose={props.onClose}
            parentOpensUpward={props.parentOpensUpward ?? false}
          />
        )}
      </Index>
    </div>
  );
};
